// Client-only WebMCP runtime.
//
// Responsibilities:
//  - detect a native `document.modelContext` implementation
//  - lazily load the @mcp-b/global polyfill when the browser has none, so an
//    MCP client bridged into the tab still sees our tools
//  - register/unregister tools through AbortSignals (the draft API's only
//    documented teardown mechanism)
//  - keep a local mirror of what is registered so the page can show a live
//    inspector without depending on `getTools()` being implemented

import type {
  ModelContextLike,
  RegisteredTool,
  WebMcpAvailability,
  WebMcpScope,
  WebMcpTool,
} from "./types";

type Listener = () => void;

const registry = new Map<string, RegisteredTool>();
const listeners = new Set<Listener>();

let availability: WebMcpAvailability = "unknown";
let ready: Promise<ModelContextLike | null> | null = null;

// useSyncExternalStore requires a stable snapshot: rebuild the array only when
// the registry actually changes, never on every read.
let snapshot: RegisteredTool[] = [];

function emit() {
  snapshot = [...registry.values()].sort((a, b) =>
    a.tool.name.localeCompare(b.tool.name),
  );
  for (const l of listeners) l();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRegisteredTools(): RegisteredTool[] {
  return snapshot;
}

export function getAvailability(): WebMcpAvailability {
  return availability;
}

function readModelContext(): ModelContextLike | null {
  if (typeof document === "undefined") return null;
  const doc = document as unknown as { modelContext?: ModelContextLike };
  if (doc.modelContext?.registerTool) return doc.modelContext;
  const nav = navigator as unknown as { modelContext?: ModelContextLike };
  if (nav?.modelContext?.registerTool) return nav.modelContext;
  return null;
}

/**
 * Resolve the model context, loading the polyfill once when the browser does
 * not ship WebMCP natively. Safe to call repeatedly; the work happens once.
 */
export function ensureModelContext(): Promise<ModelContextLike | null> {
  if (ready) return ready;
  ready = (async () => {
    if (typeof window === "undefined") {
      availability = "unavailable";
      return null;
    }
    const native = readModelContext();
    if (native) {
      availability = "native";
      emit();
      return native;
    }
    try {
      // v5 of the runtime no longer relies on import side effects alone: we
      // initialise it explicitly and pin the transport to our own origin, so
      // the tab bridge is never opened to an arbitrary embedder.
      const mod = await import("@mcp-b/global");
      if (typeof mod.initializeWebModelContext === "function") {
        try {
          mod.initializeWebModelContext({
            transport: {
              tabServer: { allowedOrigins: [window.location.origin] },
            },
          });
        } catch {
          // Already initialised (or a native implementation claimed the slot).
        }
      }
      const polyfilled = readModelContext();
      availability = polyfilled ? "polyfill" : "unavailable";
      emit();
      return polyfilled;
    } catch {
      availability = "unavailable";
      emit();
      return null;
    }
  })();
  return ready;
}

/**
 * Push the full tool set through the draft's declarative entry point.
 * `provideContext` replaces everything the page exposes, so it is always fed
 * from the local mirror rather than from one caller's slice.
 */
function syncProvideContext(ctx: ModelContextLike): boolean {
  if (typeof ctx.provideContext !== "function") return false;
  try {
    ctx.provideContext({ tools: snapshot.map((entry) => entry.tool) });
    return true;
  } catch (err) {
    console.warn("[webmcp] provideContext failed, falling back", err);
    return false;
  }
}

/**
 * Register a set of tools for as long as the caller keeps them alive.
 * Returns a disposer that removes them again, through `unregisterTool` where
 * the implementation offers it and through the AbortSignal otherwise.
 */
export function registerTools(
  tools: WebMcpTool[],
  scope: WebMcpScope,
): () => void {
  if (typeof window === "undefined" || tools.length === 0) return () => {};

  const controller = new AbortController();
  let disposed = false;

  for (const tool of tools) registry.set(tool.name, { tool, scope });
  emit();

  void ensureModelContext().then((ctx) => {
    if (!ctx || disposed) return;
    if (syncProvideContext(ctx)) return;
    for (const tool of tools) {
      try {
        ctx.registerTool(tool, { signal: controller.signal });
      } catch (err) {
        // A malformed descriptor must never take the page down.
        console.warn("[webmcp] failed to register", tool.name, err);
      }
    }
  });

  return () => {
    if (disposed) return;
    disposed = true;
    controller.abort();
    for (const tool of tools) {
      const current = registry.get(tool.name);
      if (current?.tool === tool) registry.delete(tool.name);
    }
    emit();
    void ensureModelContext().then((ctx) => {
      if (!ctx) return;
      if (syncProvideContext(ctx)) return;
      if (typeof ctx.unregisterTool !== "function") return;
      for (const tool of tools) {
        try {
          ctx.unregisterTool(tool.name);
        } catch {
          // The abort signal above already covers implementations that throw.
        }
      }
    });
  };
}

/**
 * Call a registered tool locally. Used by the on-page demo console so the
 * documentation page can exercise the exact code path an agent would hit.
 */
export async function invokeTool(
  name: string,
  input: Record<string, unknown>,
): Promise<unknown> {
  const entry = registry.get(name);
  if (!entry) throw new Error(`Unknown tool: ${name}`);
  return await entry.tool.execute(input, {});
}
